/**
 * usePublicCampusData Hook
 * 
 * Custom hook for managing public campus summary data fetching and state.
 * Handles all data loading for the public campus summary page.
 */

import { useState, useEffect, useCallback } from 'react';
import publicCampusService from '../services/publicCampusService';

export const usePublicCampusData = (slug) => {
  const [campusInfo, setCampusInfo] = useState(null);
  const [populationData, setPopulationData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [nearbySchools, setNearbySchools] = useState(null);
  const [projectionsData, setProjectionsData] = useState(null);
  
  const [selectedDriveTime, setSelectedDriveTime] = useState(10);
  const [selectedTab, setSelectedTab] = useState(0);
  
  const [loading, setLoading] = useState({
    campusInfo: true,
    population: false,
    comparison: false,
    nearbySchools: false,
    projections: false
  });
  
  const [error, setError] = useState(null);

  // Fetch campus info (only once on mount)
  useEffect(() => {
    const fetchCampusInfo = async () => {
      try {
        setLoading(prev => ({ ...prev, campusInfo: true }));
        setError(null);
        
        const info = await publicCampusService.getCampusInfo(slug);
        setCampusInfo(info);
        
      } catch (err) {
        console.error('Error fetching campus info:', err);
        setError({
          message: err.message || 'Failed to load campus information',
          status: err.status
        });
      } finally {
        setLoading(prev => ({ ...prev, campusInfo: false }));
      }
    };

    if (slug) {
      fetchCampusInfo();
    }
  }, [slug]);

  // Fetch population data when drive time changes
  const fetchPopulationData = useCallback(async () => {
    if (!campusInfo) return;
    
    try {
      setLoading(prev => ({ ...prev, population: true }));
      const data = await publicCampusService.getPopulationData(slug, selectedDriveTime);
      setPopulationData(data);
    } catch (err) {
      console.error('Error fetching population data:', err);
    } finally {
      setLoading(prev => ({ ...prev, population: false }));
    }
  }, [slug, selectedDriveTime, campusInfo]);

  // Fetch comparison data when drive time changes
  const fetchComparisonData = useCallback(async () => {
    if (!campusInfo) return;
    
    try {
      setLoading(prev => ({ ...prev, comparison: true, nearbySchools: true }));
      
      // Fetch both comparison data and nearby schools for the map
      const [compData, mapData] = await Promise.all([
        publicCampusService.getComparisonData(slug, selectedDriveTime),
        publicCampusService.getNearbySchools(slug, selectedDriveTime)
      ]);
      
      setComparisonData(compData);
      setNearbySchools(mapData);
    } catch (err) {
      console.error('Error fetching comparison data:', err);
    } finally {
      setLoading(prev => ({ ...prev, comparison: false, nearbySchools: false }));
    }
  }, [slug, selectedDriveTime, campusInfo]);

  // Fetch projections data (only once after campus info loads)
  useEffect(() => {
    const fetchProjectionsData = async () => {
      if (!campusInfo) return;
      
      try {
        setLoading(prev => ({ ...prev, projections: true }));
        const data = await publicCampusService.getProjectionsData(slug);
        setProjectionsData(data);
      } catch (err) {
        console.error('Error fetching projections data:', err);
      } finally {
        setLoading(prev => ({ ...prev, projections: false }));
      }
    };

    fetchProjectionsData();
  }, [slug, campusInfo]);

  // Fetch data based on selected tab
  useEffect(() => {
    if (!campusInfo) return;

    switch (selectedTab) {
      case 0: // Population Trends
        fetchPopulationData();
        break;
      case 1: // Area School Comparison
        fetchComparisonData();
        break;
      case 2: // Projected Enrollment
        // Projections loaded once, no need to refetch
        break;
      default:
        break;
    }
  }, [selectedTab, selectedDriveTime, campusInfo, fetchPopulationData, fetchComparisonData]);

  // Handle drive time change
  const handleDriveTimeChange = (event) => {
    setSelectedDriveTime(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return {
    // Data
    campusInfo,
    populationData,
    comparisonData,
    nearbySchools,
    projectionsData,
    
    // UI State
    selectedDriveTime,
    selectedTab,
    loading,
    error,
    
    // Handlers
    handleDriveTimeChange,
    handleTabChange,
    
    // Manual refresh functions
    refetchPopulation: fetchPopulationData,
    refetchComparison: fetchComparisonData
  };
};

export default usePublicCampusData;

